import React, { useMemo, useState } from 'react';
import { Outlet, Link, useLocation, useParams } from 'react-router-dom';
import { SignOutButton, useUser } from '@clerk/clerk-react';
import { colors } from '../theme';
import { normaliseTenant } from './utils';

const navConfig = [
  { label: 'Overview', buildPath: tenant => `/farm/${tenant}` },
  { label: 'Global Map', buildPath: tenant => `/farm/${tenant}/map` },
];

export default function FarmLayout({ role }) {
  const { tenant: rawTenant } = useParams();
  const tenant = normaliseTenant(rawTenant);
  const location = useLocation();
  const { user } = useUser();
  const [refreshToken, setRefreshToken] = useState(0);

  const requestRefresh = () => setRefreshToken(token => token + 1);

  const displayTenant = useMemo(() => {
    if (!tenant) return 'Farm';
    const name = tenant.replace(/^farm-/, '').replace(/-/g, ' ');
    return name ? name.replace(/\b\w/g, match => match.toUpperCase()) : tenant;
  }, [tenant]);

  const displayName =
    user?.fullName ||
    user?.username ||
    user?.primaryEmailAddress?.emailAddress ||
    'User';

  return (
    <div style={{ minHeight: '100vh', backgroundColor: colors.sectionBackground, color: colors.background }}>
      <header style={headerStyle}>
        <div>
          <h1 style={{ margin: 0 }}>{displayTenant} Herd Console</h1>
          <p style={{ margin: '0.25rem 0 0', fontSize: '0.9rem', color: 'rgba(255,255,255,0.8)' }}>
            Tenant role: {role || 'guest'}
          </p>
        </div>
        <div style={{ textAlign: 'right' }}>
          <div style={{ fontSize: '0.9rem', marginBottom: '0.4rem' }}>{displayName}</div>
          <SignOutButton>
            <button type="button" style={signOutButtonStyle}>Sign out</button>
          </SignOutButton>
        </div>
      </header>

      <nav style={navStyle}>
        {navConfig.map(item => {
          const path = item.buildPath(tenant);
          const isActive =
            location.pathname === path ||
            (path !== `/farm/${tenant}` && location.pathname.startsWith(path));
          return (
            <Link
              key={item.label}
              to={path}
              style={{
                ...navLinkStyle,
                backgroundColor: isActive ? colors.primary : 'transparent',
                color: isActive ? colors.background : colors.primary,
                borderColor: colors.primary,
              }}
            >
              {item.label}
            </Link>
          );
        })}
      </nav>

      <main style={mainStyle}>
        <Outlet context={{ tenant, role, refreshToken, requestRefresh }} />
      </main>
    </div>
  );
}

const headerStyle = {
  backgroundColor: colors.primary,
  color: colors.background,
  padding: '1.25rem 1.5rem',
  display: 'flex',
  justifyContent: 'space-between',
  alignItems: 'center',
  flexWrap: 'wrap',
  gap: '1rem',
};

const navStyle = {
  display: 'flex',
  gap: '1rem',
  padding: '1rem 1.5rem',
  flexWrap: 'wrap',
};

const navLinkStyle = {
  padding: '0.5rem 1rem',
  borderRadius: '999px',
  border: '1px solid transparent',
  fontWeight: 600,
  textDecoration: 'none',
  transition: 'background-color 0.2s ease',
};

const signOutButtonStyle = {
  backgroundColor: 'rgba(255,255,255,0.15)',
  border: 'none',
  borderRadius: '999px',
  padding: '0.35rem 0.9rem',
  color: colors.background,
  cursor: 'pointer',
};

const mainStyle = {
  backgroundColor: colors.background,
  color: colors.text,
  margin: '0 1.5rem 2rem',
  borderRadius: '16px',
  padding: '1.5rem',
  boxShadow: '0 8px 20px rgba(0,0,0,0.12)',
  minHeight: '65vh',
};
